import React from "react";
import Resources from "../../../types/Resources";
import { Modal } from "react-bootstrap";
import ChecklistRadioOptions from "../../Common/ChecklistRadioOptions";
import Take2ChecklistFormState from "../../../types/forms/Permits/Conditions/Take2ChecklistFormState";
import TextArea from "../../Common/TextArea";

type Take2ChecklistModalProps = {
    checklistVersion: number;
    formState: Take2ChecklistFormState;
    show: boolean;
    onHide: () => void;
    resources: Resources;
    onChange: (fn: (prevValue: Take2ChecklistFormState) => Take2ChecklistFormState) => void;
    canManageContent: boolean;
};

function Take2ChecklistModal({
    checklistVersion,
    resources,
    onChange,
    formState,
    show,
    onHide,
    canManageContent
}: Take2ChecklistModalProps) {
    function take2ChecklistChangeHandler(key: keyof Take2ChecklistFormState, value: any) {
        onChange((prevValue) => {
            const updatedFormState: Take2ChecklistFormState = {
                ...prevValue,
                [key]: { ...prevValue[key], value }
            };

            return updatedFormState;
        });
    }

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <h1 className="h5">{resources.get("CheckListForTake2")}</h1>
            </Modal.Header>
            <Modal.Body>
                <h1 className="h6">{resources.get("Take2")}</h1>
                <p>{resources.get("AlarmInformation")}</p>
                {checklistVersion === 0 ? (
                    <Take2ChecklistVersion0
                        resources={resources}
                        canManageContent={canManageContent}
                        changeHandler={take2ChecklistChangeHandler}
                        formState={formState}
                    />
                ) : (
                    <Take2ChecklistVersion2026_02
                        resources={resources}
                        canManageContent={canManageContent}
                        changeHandler={take2ChecklistChangeHandler}
                        formState={formState}
                    />
                )}
                <div className="border-top m-3">
                    <h1 className="h6">{resources.get("RiskObservation")}</h1>
                    <p>{resources.get("RiskObservationInformation")}</p>
                    <div className="row">
                        <div className="col-6">
                            <label htmlFor="risk-observation-date-time">{resources.get("DateAndTime")}</label>
                            <input
                                id="risk-observation-date-time"
                                className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                                type="datetime-local"
                                value={formState.riskObservationDateTime.value ?? ""}
                                onChange={(e) => take2ChecklistChangeHandler("riskObservationDateTime", e.target.value)}
                                readOnly={!canManageContent}
                            />
                        </div>
                        <div className="col-6">
                            <label htmlFor="risk-observation-location">{resources.get("Location")}</label>
                            <input
                                id="risk-observation-location"
                                className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                                type="text"
                                value={formState.riskObservationLocation.value ?? ""}
                                onChange={(e) => take2ChecklistChangeHandler("riskObservationLocation", e.target.value)}
                                readOnly={!canManageContent}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <label htmlFor="what-RTO">{resources.get("WhatRTO")}</label>
                            <input
                                id="what-RTO"
                                className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                                type="text"
                                value={formState.whatRTO.value ?? ""}
                                onChange={(e) => take2ChecklistChangeHandler("whatRTO", e.target.value)}
                                readOnly={!canManageContent}
                            />
                        </div>
                        <div className="col-6">
                            <label htmlFor="risk-observation-notifier">{resources.get("Notifier")}</label>
                            <input
                                id="risk-observation-notifier"
                                className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                                type="text"
                                value={formState.riskObservationNotifier.value ?? ""}
                                onChange={(e) => take2ChecklistChangeHandler("riskObservationNotifier", e.target.value)}
                                readOnly={!canManageContent}
                            />
                        </div>
                    </div>
                    <div className="mt-2">
                        <label htmlFor="risk-observation-course-of-events">
                            {resources.get("DescribeCourseOfEvents")}
                        </label>
                        <textarea
                            id="risk-observation-course-of-events"
                            className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                            rows={3}
                            value={formState.riskObservationCourseOfEvents.value ?? ""}
                            onChange={(e) =>
                                take2ChecklistChangeHandler("riskObservationCourseOfEvents", e.target.value)
                            }
                            readOnly={!canManageContent}
                        ></textarea>
                    </div>
                    <div className="mt-2">
                        <label htmlFor="risk-observation-cause-description">
                            {resources.get("YourUnderstandingOfCause")}
                        </label>
                        <textarea
                            id="risk-observation-cause-description"
                            className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                            rows={3}
                            value={formState.riskObservationCauseDescription.value ?? ""}
                            onChange={(e) =>
                                take2ChecklistChangeHandler("riskObservationCauseDescription", e.target.value)
                            }
                            readOnly={!canManageContent}
                        ></textarea>
                    </div>
                    <div className="mt-2">
                        <label htmlFor="risk-observation-fix-suggestion">{resources.get("SuggestionOfFix")}:</label>
                        <textarea
                            id="risk-observation-fix-suggestion"
                            className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                            rows={3}
                            value={formState.riskObservationFixSuggestion.value ?? ""}
                            onChange={(e) =>
                                take2ChecklistChangeHandler("riskObservationFixSuggestion", e.target.value)
                            }
                            readOnly={!canManageContent}
                        ></textarea>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

type ChecklistProps = {
    resources: Resources;
    formState: Take2ChecklistFormState;
    changeHandler: (key: keyof Take2ChecklistFormState, value: any) => void;
    canManageContent: boolean;
};

function Take2ChecklistVersion0({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-3">
                <ChecklistRadioOptions
                    id="right-place-and-equipment"
                    labelText={resources.get("RightPlaceAndEquipment")}
                    onChange={(updatedValue) => changeHandler("rightPlaceAndEquipment", updatedValue)}
                    resources={resources}
                    value={formState.rightPlaceAndEquipment.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="have-understood-instructions"
                    labelText={resources.get("HaveUnderstoodInstructions")}
                    onChange={(updatedValue) => changeHandler("haveUnderstoodInstructions", updatedValue)}
                    resources={resources}
                    value={formState.haveUnderstoodInstructions.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="have-read-permit-and-terms"
                    labelText={resources.get("HaveReadPermitAndTerms")}
                    onChange={(updatedValue) => changeHandler("haveReadPermitAndTerms", updatedValue)}
                    resources={resources}
                    value={formState.haveReadPermitAndTerms.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="have-protection-equipment"
                    labelText={resources.get("HaveProtectionEquipment")}
                    onChange={(updatedValue) => changeHandler("haveProtectionEquipment", updatedValue)}
                    resources={resources}
                    value={formState.haveProtectionEquipment.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="have-equipment"
                    labelText={resources.get("HaveEquipment")}
                    onChange={(updatedValue) => changeHandler("haveEquipment", updatedValue)}
                    resources={resources}
                    value={formState.haveEquipment.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="is-nearby-work"
                    labelText={resources.get("IsNearbyWork")}
                    onChange={(updatedValue) => changeHandler("isNearbyWork", updatedValue)}
                    resources={resources}
                    value={formState.isNearbyWork.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="is-equipment-drained"
                    labelText={resources.get("IsEquipmentDrainedAndNoPreassure")}
                    onChange={(updatedValue) => changeHandler("equipmentIsDrainedAndNoPreassure", updatedValue)}
                    resources={resources}
                    value={formState.equipmentIsDrainedAndNoPreassure.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="duty-switch-is-disconnected"
                    labelText={resources.get("IsDutySwitchDisconnected")}
                    onChange={(updatedValue) => changeHandler("dutySwitchIsDisconnected", updatedValue)}
                    resources={resources}
                    value={formState.dutySwitchIsDisconnected.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="risk-of-burns-crush-cuts"
                    labelText={resources.get("IsRiskOfBurnsCrushCuts")}
                    onChange={(updatedValue) => changeHandler("riskOfBurnsCrushCuts", updatedValue)}
                    resources={resources}
                    value={formState.riskOfBurnsCrushCuts.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="risk-of-falls-or-falling-objects"
                    labelText={resources.get("IsRiskOfFallsOrFallingObjects")}
                    onChange={(updatedValue) => changeHandler("riskOfFallsOrFallingObjects", updatedValue)}
                    resources={resources}
                    value={formState.riskOfFallsOrFallingObjects.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="needs-blocking-off"
                    labelText={resources.get("NeedsBlockingOff")}
                    onChange={(updatedValue) => changeHandler("needsBlockingOff", updatedValue)}
                    resources={resources}
                    value={formState.needsBlockingOff.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="has-appropriate-scaffolding"
                    labelText={resources.get("HasAppropriateScaffolding")}
                    onChange={(updatedValue) => changeHandler("hasAppropriateScaffolding", updatedValue)}
                    resources={resources}
                    value={formState.hasAppropriateScaffolding.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="risk-of-fire"
                    labelText={resources.get("RiskOfFire")}
                    onChange={(updatedValue) => changeHandler("riskOfFire", updatedValue)}
                    resources={resources}
                    value={formState.riskOfFire.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <TextArea
                    labelText={resources.get("AtWorkOnProcessEquipmentWhichMedia")}
                    errorMessage={formState.whichProcessEquipmentMediaDescription.errorMessage}
                    onChange={(updatedValue) => changeHandler("whichProcessEquipmentMediaDescription", updatedValue)}
                    textAreaId="which-process-equipment-media-description"
                    value={formState.whichProcessEquipmentMediaDescription.value}
                    boldLabel={false}
                    rows={2}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <TextArea
                    errorMessage={formState.whereIsClosestEyeWashAndShowerDescription.errorMessage}
                    labelText={resources.get("WhereIsClosestEyeWashAndShower")}
                    onChange={(updatedValue) =>
                        changeHandler("whereIsClosestEyeWashAndShowerDescription", updatedValue)
                    }
                    readOnly={!canManageContent}
                    textAreaId="where-is-closest-eye-wash-and-shower-description"
                    value={formState.whereIsClosestEyeWashAndShowerDescription.value}
                    boldLabel={false}
                    rows={2}
                />
            </li>
            <li className="m-3">
                <TextArea
                    errorMessage={formState.whereIsClosestExtinguishingEquipmentDescription.errorMessage}
                    labelText={resources.get("RiskOfFire")}
                    readOnly={!canManageContent}
                    onChange={(updatedValue) =>
                        changeHandler("whereIsClosestExtinguishingEquipmentDescription", updatedValue)
                    }
                    textAreaId="where-is-closest-extinguishing-equipment-description"
                    value={formState.whereIsClosestExtinguishingEquipmentDescription.value}
                    boldLabel={false}
                    rows={2}
                />
            </li>
            <li className="m-3">
                <TextArea
                    errorMessage={formState.whereIsClosestAlarmEquipmentDescription.errorMessage}
                    labelText={resources.get("WhereIsClosestAlarmEquipment")}
                    onChange={(updatedValue) => changeHandler("whereIsClosestAlarmEquipmentDescription", updatedValue)}
                    readOnly={!canManageContent}
                    textAreaId="where-is-closest-alarm-equipment-description"
                    value={formState.whereIsClosestAlarmEquipmentDescription.value}
                    boldLabel={false}
                    rows={2}
                />
            </li>
        </ul>
    );
}

function Take2ChecklistVersion2026_02({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-3">
                <ChecklistRadioOptions
                    id="know-where-closest-alarm-equipment-is"
                    labelText={resources.get("KnowWhereClosestAlarmEquipmentIs")}
                    onChange={(updatedValue) => changeHandler("knowWhereClosestAlarmEquipmentIs", updatedValue)}
                    resources={resources}
                    value={formState.knowWhereClosestAlarmEquipmentIs?.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="right-place-and-equipment"
                    labelText={resources.get("RightPlaceAndEquipment")}
                    onChange={(updatedValue) => changeHandler("rightPlaceAndEquipment", updatedValue)}
                    resources={resources}
                    value={formState.rightPlaceAndEquipment.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="have-understood-instructions"
                    labelText={resources.get("HaveUnderstoodInstructions")}
                    onChange={(updatedValue) => changeHandler("haveUnderstoodInstructions", updatedValue)}
                    resources={resources}
                    value={formState.haveUnderstoodInstructions.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="have-read-permit-and-terms"
                    labelText={resources.get("HaveReadPermitAndTerms")}
                    onChange={(updatedValue) => changeHandler("haveReadPermitAndTerms", updatedValue)}
                    resources={resources}
                    value={formState.haveReadPermitAndTerms.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="have-protection-equipment"
                    labelText={resources.get("HaveProtectionEquipment")}
                    onChange={(updatedValue) => changeHandler("haveProtectionEquipment", updatedValue)}
                    resources={resources}
                    value={formState.haveProtectionEquipment.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="have-equipment"
                    labelText={resources.get("HaveEquipment")}
                    onChange={(updatedValue) => changeHandler("haveEquipment", updatedValue)}
                    resources={resources}
                    value={formState.haveEquipment.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="is-nearby-work"
                    labelText={resources.get("IsNearbyWork")}
                    onChange={(updatedValue) => changeHandler("isNearbyWork", updatedValue)}
                    resources={resources}
                    value={formState.isNearbyWork.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="have-knowledge-of-process-equipment-media"
                    labelText={resources.get("HaveKnowledgeOfProcessEquipmentMedia")}
                    onChange={(updatedValue) => changeHandler("haveKnowledgeOfProcessEquipmentMedia", updatedValue)}
                    resources={resources}
                    value={formState.haveKnowledgeOfProcessEquipmentMedia?.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="is-equipment-drained"
                    labelText={resources.get("IsEquipmentDrainedAndNoPreassure")}
                    onChange={(updatedValue) => changeHandler("equipmentIsDrainedAndNoPreassure", updatedValue)}
                    resources={resources}
                    value={formState.equipmentIsDrainedAndNoPreassure.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="duty-switch-is-disconnected"
                    labelText={resources.get("IsDutySwitchDisconnected")}
                    onChange={(updatedValue) => changeHandler("dutySwitchIsDisconnected", updatedValue)}
                    resources={resources}
                    value={formState.dutySwitchIsDisconnected.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="risk-of-burns-crush-cuts"
                    labelText={resources.get("IsRiskOfBurnsCrushCuts")}
                    onChange={(updatedValue) => changeHandler("riskOfBurnsCrushCuts", updatedValue)}
                    resources={resources}
                    value={formState.riskOfBurnsCrushCuts.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="risk-of-falls-or-falling-objects"
                    labelText={resources.get("IsRiskOfFallsOrFallingObjects")}
                    onChange={(updatedValue) => changeHandler("riskOfFallsOrFallingObjects", updatedValue)}
                    resources={resources}
                    value={formState.riskOfFallsOrFallingObjects.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="needs-workplace-blocking-off"
                    labelText={resources.get("NeedsWorkplaceBlockingOff")}
                    onChange={(updatedValue) => changeHandler("needsWorkplaceBlockingOff", updatedValue)}
                    resources={resources}
                    value={formState.needsWorkplaceBlockingOff?.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="has-appropriate-scaffolding"
                    labelText={resources.get("HasAppropriateScaffolding")}
                    onChange={(updatedValue) => changeHandler("hasAppropriateScaffolding", updatedValue)}
                    resources={resources}
                    value={formState.hasAppropriateScaffolding.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="know-where-closest-eye-wash-and-shower-is"
                    labelText={resources.get("KnowWhereClosestEyeWashAndShowerIs")}
                    onChange={(updatedValue) => changeHandler("knowWhereClosestEyeWashAndShowerIs", updatedValue)}
                    resources={resources}
                    value={formState.knowWhereClosestEyeWashAndShowerIs?.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="has-fire-risk"
                    labelText={resources.get("HasFireRisk")}
                    onChange={(updatedValue) => changeHandler("hasFireRisk", updatedValue)}
                    resources={resources}
                    value={formState.hasFireRisk?.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="know-where-closest-extinguishing-equipment-is"
                    labelText={resources.get("KnowWhereClosestExtinguishingEquipmentIs")}
                    onChange={(updatedValue) =>
                        changeHandler("knowWhereClosestExtinguishingEquipmentIs", updatedValue)
                    }
                    resources={resources}
                    value={formState.knowWhereClosestExtinguishingEquipmentIs?.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <TextArea
                    labelText={resources.get("AtWorkOnProcessEquipmentWhichMedia")}
                    errorMessage={formState.whichProcessEquipmentMediaDescription.errorMessage}
                    onChange={(updatedValue) => changeHandler("whichProcessEquipmentMediaDescription", updatedValue)}
                    textAreaId="which-process-equipment-media-description"
                    value={formState.whichProcessEquipmentMediaDescription.value}
                    boldLabel={false}
                    rows={2}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <TextArea
                    errorMessage={formState.whereIsClosestEyeWashAndShowerDescription.errorMessage}
                    labelText={resources.get("WhereIsClosestEyeWashAndShower")}
                    onChange={(updatedValue) =>
                        changeHandler("whereIsClosestEyeWashAndShowerDescription", updatedValue)
                    }
                    readOnly={!canManageContent}
                    textAreaId="where-is-closest-eye-wash-and-shower-description"
                    value={formState.whereIsClosestEyeWashAndShowerDescription.value}
                    boldLabel={false}
                    rows={2}
                />
            </li>
            <li className="m-3">
                <TextArea
                    errorMessage={formState.whereIsClosestExtinguishingEquipmentDescription.errorMessage}
                    labelText={resources.get("RiskOfFire")}
                    readOnly={!canManageContent}
                    onChange={(updatedValue) =>
                        changeHandler("whereIsClosestExtinguishingEquipmentDescription", updatedValue)
                    }
                    textAreaId="where-is-closest-extinguishing-equipment-description"
                    value={formState.whereIsClosestExtinguishingEquipmentDescription.value}
                    boldLabel={false}
                    rows={2}
                />
            </li>
            <li className="m-3">
                <TextArea
                    errorMessage={formState.whereIsClosestAlarmEquipmentDescription.errorMessage}
                    labelText={resources.get("WhereIsClosestAlarmEquipment")}
                    onChange={(updatedValue) => changeHandler("whereIsClosestAlarmEquipmentDescription", updatedValue)}
                    readOnly={!canManageContent}
                    textAreaId="where-is-closest-alarm-equipment-description"
                    value={formState.whereIsClosestAlarmEquipmentDescription.value}
                    boldLabel={false}
                    rows={2}
                />
            </li>
        </ul>
    );
}

export default Take2ChecklistModal;
